import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import clientService from '../../services/clientService';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { FaUser, FaSave, FaTimes, FaBuilding, FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import CountrySelector from '../../components/common/CountrySelector';
import { useI18n } from '../../i18n/I18nContext';
import './ClientForm.css';

function ClientForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const { t } = useI18n();

    const [formData, setFormData] = useState({
        nom: '',
        prenom: '',
        email: '',
        emailFacturation: '',
        telephone: '',
        entreprise: '',
        adresse: ''
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isEditMode) {
            const fetchClient = async () => {
                try {
                    setLoading(true);
                    const client = await clientService.getClientById(id);
                    setFormData(client);
                    setError(null);
                } catch (err) {
                    setError(t('client.loadFailed'));
                } finally {
                    setLoading(false);
                }
            };

            fetchClient();
        }
    }, [id, isEditMode, t]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            if (isEditMode) {
                await clientService.updateClient(id, formData);
            } else {
                await clientService.createClient(formData);
            }
            navigate('/clients');
        } catch (err) {
            setError(t('client.saveFailed'));
            setLoading(false);
        }
    };

    if (loading && isEditMode) {
        return (
            <DashboardLayout>
                <div className="container mt-4">
                    <div className="d-flex justify-content-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="client-form-container">
                <div className="client-form-card">
                    <div className="client-form-header">
                        <h2 className="client-form-title">
                            <FaUser className="profile-icon" />
                            {isEditMode ? t('client.editClient') : t('client.addClient')}
                        </h2>
                    </div>

                    {error && (
                        <div className="alert alert-danger alert-custom" role="alert">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="client-form-grid">
                            {/* Left column */}
                            <div className="form-column">
                                {/* Section: Identification */}
                                <div className="form-section">
                                    <h3 className="section-title">
                                        <FaUser className="me-2" /> {t('client.sections.identification')}
                                    </h3>
                                    <div className="field-grid">
                                        <div className="field-item">
                                            <div className="form-floating">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="prenom"
                                                    name="prenom"
                                                    value={formData.prenom}
                                                    onChange={handleChange}
                                                    required
                                                    placeholder={t('client.prenom')}
                                                />
                                                <label htmlFor="prenom">{t('client.prenom')} *</label>
                                            </div>
                                        </div>
                                        <div className="field-item">
                                            <div className="form-floating">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="nom"
                                                    name="nom"
                                                    value={formData.nom}
                                                    onChange={handleChange}
                                                    required
                                                    placeholder={t('client.nom')}
                                                />
                                                <label htmlFor="nom">{t('client.nom')} *</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Section: Contact */}
                                <div className="form-section">
                                    <h3 className="section-title">
                                        <FaPhone className="me-2" /> {t('client.sections.details')}
                                    </h3>
                                    <div className="field-grid">
                                        <div className="field-item">
                                            <div className="form-floating">
                                                <input
                                                    type="tel"
                                                    className="form-control"
                                                    id="telephone"
                                                    name="telephone"
                                                    value={formData.telephone}
                                                    onChange={handleChange}
                                                    required
                                                    placeholder={t('client.telephone')}
                                                />
                                                <label htmlFor="telephone">{t('client.telephone')} *</label>
                                            </div>
                                        </div>
                                        <div className="field-item">
                                            <div className="form-floating">
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    id="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    placeholder={t('client.email')}
                                                />
                                                <label htmlFor="email">{t('client.email')}</label>
                                            </div>
                                        </div>
                                        <div className="field-item col-12">
                                            <div className="form-floating">
                                                <textarea
                                                    className="form-control"
                                                    id="adresse"
                                                    name="adresse"
                                                    style={{ height: '6.25rem' }}
                                                    value={formData.adresse}
                                                    onChange={handleChange}
                                                    placeholder={t('client.adresse')}
                                                ></textarea>
                                                <label htmlFor="adresse"><FaMapMarkerAlt className="me-1" />{t('client.adresse')}</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right column */}
                            <div className="form-column">
                                {/* Section: Company */}
                                <div className="form-section">
                                    <h3 className="section-title">
                                        <FaBuilding className="me-2" /> {t('client.entreprise')}
                                    </h3>
                                    <div className="form-floating">
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="entreprise"
                                            name="entreprise"
                                            value={formData.entreprise}
                                            onChange={handleChange}
                                            placeholder={t('client.entreprise')}
                                        />
                                        <label htmlFor="entreprise">{t('client.entreprise')}</label>
                                    </div>
                                </div>

                                {/* Section: Billing */}
                                <div className="form-section">
                                    <h3 className="section-title">
                                        <FaEnvelope className="me-2" /> {t('client.sections.billing')}
                                    </h3>
                                    <div className="field-grid">
                                        <div className="field-item">
                                            <div className="form-floating">
                                                <select
                                                    className="form-select"
                                                    id="billingFrequency"
                                                    name="billingFrequency"
                                                    value={formData.billingFrequency || ''}
                                                    onChange={handleChange}
                                                >
                                                    <option value="">{t('common.select')}</option>
                                                    <option value="DAILY">{t('client.frequency.daily')}</option>
                                                    <option value="WEEKLY">{t('client.frequency.weekly')}</option>
                                                    <option value="FORTNIGHTLY">{t('client.frequency.fortnightly')}</option>
                                                    <option value="MONTHLY">{t('client.frequency.monthly')}</option>
                                                    <option value="PER_MISSION">{t('client.frequency.mission')}</option>
                                                </select>
                                                <label htmlFor="billingFrequency">{t('client.billingFrequency')}</label>
                                            </div>
                                        </div>
                                        <div className="field-item">
                                            <div className="form-floating">
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    id="emailFacturation"
                                                    name="emailFacturation"
                                                    value={formData.emailFacturation || ''}
                                                    onChange={handleChange}
                                                    placeholder={formData.email || t('client.emailFacturation')}
                                                />
                                                <label htmlFor="emailFacturation">
                                                    <FaEnvelope className="me-1" />
                                                    {t('client.emailFacturation')}
                                                </label>
                                            </div>
                                            {!formData.emailFacturation && formData.email && (
                                                <div className="form-text text-muted small mt-1 ms-1">
                                                    <i className="bi bi-info-circle me-1"></i>
                                                    {t('client.fallbackLabel') || 'Utilise l\'e-mail principal par défaut'}
                                                </div>
                                            )}
                                        </div>
                                        <div className="field-item">
                                            <div className="form-floating">
                                                <select
                                                    className="form-select"
                                                    id="preferredCurrency"
                                                    name="preferredCurrency"
                                                    value={formData.preferredCurrency || 'EUR'}
                                                    onChange={handleChange}
                                                >
                                                    <option value="EUR">EUR</option>
                                                    <option value="MAD">MAD</option>
                                                    <option value="USD">USD</option>
                                                </select>
                                                <label htmlFor="preferredCurrency">{t('client.currency')}</label>
                                            </div>
                                        </div>
                                        <div className="field-item">
                                            <CountrySelector
                                                value={formData.country}
                                                onChange={(val) => setFormData(prev => ({ ...prev, country: val }))}
                                                label={t('client.country')}
                                            />
                                        </div>
                                        <div className="field-item">
                                            <div className="form-floating">
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    id="paymentTerms"
                                                    name="paymentTerms"
                                                    value={formData.paymentTerms || 30}
                                                    onChange={handleChange}
                                                    min="0"
                                                    placeholder={t('client.paymentTerms')}
                                                />
                                                <label htmlFor="paymentTerms">{t('client.paymentTerms')}</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Actions — spans both columns */}
                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => navigate('/clients')}
                                >
                                    <FaTimes className="me-2" /> {t('common.close')}
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-save"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    ) : (
                                        <FaSave className="me-2" />
                                    )}
                                    {t('common.save')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </DashboardLayout>
    );
}

export default ClientForm;
